<template>
    <div>
      <Toast position="top-right" />
      <ConfirmDialog></ConfirmDialog>
      
      <Toolbar class="mb-4 surface-0">
        <template #start>
          <h1 class="text-2xl font-bold text-800">Gestión de Delegaciones</h1>
        </template>
        <template #end>
          <Button 
            label="Nueva Delegación" 
            icon="pi pi-plus" 
            class="p-button-raised p-button-success" 
            @click="openNew" 
            :disabled="loading"
          />
        </template>
      </Toolbar>
  
      <ProgressBar v-if="initialLoading" mode="indeterminate" class="my-2" />
  
      <div class="grid">
        <div class="col-12">
          <Card class="shadow-3">
            <template #content>
              <DataTable 
                :value="delegaciones" 
                :loading="loading" 
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                :rowsPerPageOptions="[5,10,25]"
                currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} delegaciones"
                responsiveLayout="scroll"
                stripedRows
                removableSort
                class="p-datatable-sm"
                scrollable="true"
                scrollHeight="400px"
                v-model:filters="filters"
                :globalFilterFields="[ 'Descripcion', 'EstadoNombre', 'No_delegacion', 'Direccion', 'Titular']"
              >
                <template #header>
                  <div class="table-header">
                    <span class="p-input-icon-left">
                      <i class="pi pi-search" />
                      <InputText
                        type="text"
                        v-model="filters['global'].value"
                        placeholder="Buscar..."
                      />
                    </span>
                  </div>
                </template>
                <!-- <Column field="Id_delegacion" header="ID" :sortable="true" headerStyle="width: 80px"></Column> -->
                <Column field="EstadoNombre" header="Estado" :sortable="true">
                  <template #body="{data}">
                    <Tag :value="data.EstadoNombre" :severity="getEstadoSeverity(data.Id_estado)" />
                  </template>
                </Column>
                <Column field="No_delegacion" header="No. Delegación" :sortable="true"></Column>
                <Column field="Descripcion" header="Descripción" :sortable="true"></Column>
                <Column field="Direccion" header="Dirección" :sortable="true"></Column>
                <Column field="Titular" header="Titular" :sortable="true"></Column>
                <Column header="Acciones" headerStyle="width: 120px; text-align: center" bodyStyle="text-align: center">
                  <template #body="slotProps">
                    <div class="flex justify-content-center gap-1">
                      <Button 
                        icon="pi pi-pencil" 
                        class="p-button-rounded p-button-success" 
                        v-tooltip.top="'Editar'"
                        @click="editDelegacion(slotProps.data)" 
                      />
                      <Button 
                        icon="pi pi-trash" 
                        class="p-button-rounded p-button-danger" 
                        v-tooltip.top="'Eliminar'"
                        @click="confirmDeleteDelegacion(slotProps.data)" 
                      />
                    </div>
                  </template>
                </Column>
                <template #empty>
                  <div class="text-center p-4">
                    <p>{{ loading ? 'Cargando datos...' : 'No se encontraron delegaciones' }}</p>
                  </div>
                </template>
                <template #loading>
                  <div class="text-center p-4">
                    <ProgressSpinner style="width: 50px; height: 50px" />
                    <p>Cargando datos...</p>
                  </div>
                </template>
              </DataTable>
            </template>
          </Card>
        </div>
      </div>
  
      <!-- Diálogo para Crear/Editar -->
      <Dialog 
        v-model:visible="delegacionDialog" 
        :style="{width: '600px'}" 
        :header="modoEdicion ? 'Editar Delegación' : 'Nueva Delegación'" 
        :modal="true" 
        :closable="false"
        class="p-fluid"
      >
        <div class="grid formgrid p-fluid">
          <div class="field col-12">
            <label for="estado" class="block font-medium mb-2">Estado <span class="text-red-500">*</span></label>
            <Dropdown 
              id="estado"
              v-model="delegacion.Id_estado" 
              :options="estados" 
              optionLabel="Nombre" 
              optionValue="Id_estado"
              placeholder="Seleccione un estado"
              :class="{'p-invalid': submitted && !delegacion.Id_estado}"
              class="w-full"
              panelClass="shadow-3"
              :filter="true"
              :loading="loadingEstados"
            />
            <small class="p-error" v-if="submitted && !delegacion.Id_estado">El estado es requerido.</small>
          </div>
  
          <div class="field col-12">
            <label for="no_delegacion" class="block font-medium mb-2">Numero Delegacion</label>
            <InputText 
              id="no_delegacion" 
              v-model.trim="delegacion.No_delegacion" 
              class="w-full"
              placeholder="Ingrese No. Delegación"
              :class="{'p-invalid': submitted && !delegacion.No_delegacion}"
            />
            <small class="p-error" v-if="submitted && !delegacion.No_delegacion">Este campo es requerido.</small>
          </div>

          <div class="field col-12">
            <label for="descripcion" class="block font-medium mb-2">Descripción</label>
            <InputText 
              id="descripcion" 
              v-model.trim="delegacion.Descripcion" 
              class="w-full"
              placeholder="Ingrese descripción"
              :class="{'p-invalid': submitted && !delegacion.Descripcion}"
            />
            <small class="p-error" v-if="submitted && !delegacion.Descripcion">Este campo es requerido.</small>
          </div>
  
          <div class="field col-12">
            <label for="direccion" class="block font-medium mb-2">Dirección</label>
            <InputText 
              id="direccion" 
              v-model.trim="delegacion.Direccion" 
              class="w-full"
              placeholder="Ingrese dirección"
            />
          </div>
  
          <div class="field col-12">
            <label for="titular" class="block font-medium mb-2">Titular</label>
            <InputText 
              id="titular" 
              v-model.trim="delegacion.Titular" 
              class="w-full"
              placeholder="Ingrese titular"
            />
          </div>
        </div>
  
        <template #footer>
          <div class="flex justify-content-between">
            <Button 
              label="Cancelar" 
              icon="pi pi-times" 
              class="p-button-text p-button-danger" 
              @click="hideDialog" 
              :disabled="saving"
            />
            <Button 
              :label="modoEdicion ? 'Actualizar' : 'Guardar'" 
              icon="pi pi-check" 
              class="p-button-text p-button-success" 
              @click="saveDelegacion" 
              :loading="saving"
            />
          </div>
        </template>
      </Dialog>
    </div>
  </template>
  
  <script>
  import DelegacionesService from '../../service/DelegacionesService'
  import EstadosService from '../../service/EstadosService'
  import { FilterMatchMode } from "primevue/api";
  
  export default {
    data() {
      return {
        // Datos
        filters: {
          global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        },
        delegaciones: [],
        estados: [],
        
        // Estado del componente
        initialLoading: false,
        loading: false,
        loadingEstados: false,
        saving: false,
        modoEdicion: false,
        
        // UI Controls
        delegacionDialog: false,
        submitted: false,
        
        // Modelos
        delegacion: {
          Id_estado: null,
          Descripcion: '',
          Direccion: '',
          Titular: ''
        }
      }
    },
    created() {
      this.loadInitialData()
    },
    methods: {
      loadInitialData() {
        var t = this;
        t.initialLoading = true;
        
        // Cargar estados primero
        t.loadEstados().then(function() {
          // Luego cargar delegaciones
          t.loadDelegaciones().then(function() {
            t.initialLoading = false;
          }).catch(function(error) {
            t.showError('Error al cargar las delegaciones');
            t.initialLoading = false;
            console.error(error);
          });
        }).catch(function(error) {
          t.showError('Error al cargar los estados');
          t.initialLoading = false;
          console.error(error);
        });
      },
      
      loadEstados() {
        var t = this;
        t.loadingEstados = true;
        
        return new EstadosService().getEstados(t).then(function(response) {
          t.estados = response || [];
        }).catch(function(error) {
          t.showError('Error al cargar los estados');
          console.error(error);
          return [];
        }).finally(function() {
          t.loadingEstados = false;
        });
      },
      
      loadDelegaciones() {
        var t = this;
        t.loading = true;

        return new DelegacionesService().getDelegaciones(t).then(function(response) {
          t.delegaciones = (response || [])
            .map(function(d) {
              return {
                ...d,
                EstadoNombre: t.getEstadoNombre(d.Id_estado)
              };
            })
            .sort(function(a, b) {
              return a.EstadoNombre.localeCompare(b.EstadoNombre);
            });
        }).catch(function(error) {
          t.showError('Error al cargar las delegaciones');
          console.error(error);
          return [];
        }).finally(function() {
          t.loading = false;
        });
      },

      getEstadoNombre(idEstado) {
        const estado = this.estados.find(e => e.Id_estado === idEstado);
        return estado ? estado.Nombre : 'Desconocido';
      },
      
      getEstadoSeverity(idEstado) {
        const severities = ['success', 'info', 'warning', 'danger'];
        return severities[idEstado % severities.length] || 'info';
      },
      
      openNew() {
        this.delegacion = {
          Id_estado: null,
          Descripcion: '',
          Direccion: '',
          Titular: ''
        };
        this.submitted = false;
        this.modoEdicion = false;
        this.delegacionDialog = true;
      },
      
      hideDialog() {
        this.delegacionDialog = false;
        this.submitted = false;
      },
      
      editDelegacion(delegacion) {
        this.delegacion = { ...delegacion };
        this.submitted = false;
        this.modoEdicion = true;
        this.delegacionDialog = true;
      },
      
      saveDelegacion() {
        var t = this;
        t.submitted = true;
        
        // Validación básica
        if (!t.delegacion.Id_estado) {
          t.showError('El estado es requerido');
          return;
        }

        if (!t.delegacion.No_delegacion || !t.delegacion.Descripcion) {
          t.showError('El numero de delegación y descripción son requeridos');
          return;
        }
        
        t.saving = true;
        
        var delegacionService = new DelegacionesService();
        var operacion = t.modoEdicion 
          ? delegacionService.updateDelegacion(t, t.delegacion)
          : delegacionService.addDelegacion(t, t.delegacion);
        
        operacion.then(function() {
          var mensaje = t.modoEdicion 
            ? 'Delegación actualizada correctamente' 
            : 'Delegación creada correctamente';
          
          t.showSuccess(mensaje);
          t.hideDialog();
          t.loadDelegaciones();
        }).catch(function(error) {
          t.showError(error.response?.data?.message || 'Error al guardar la delegación');
          console.error(error);
        }).finally(function() {
          t.saving = false;
        });
      },
      
      confirmDeleteDelegacion(delegacion) {
        var t = this;
        
        t.$confirm.require({
          message: '¿Estás seguro de eliminar la delegación ' + delegacion.Descripcion + '?',
          header: 'Confirmar Eliminación',
          icon: 'pi pi-exclamation-triangle',
          accept: function() {
            t.deleteDelegacion(delegacion.Id_delegacion);
          }
        });
      },
      
      deleteDelegacion(id) {
        var t = this;
        t.loading = true;
        
        new DelegacionesService().deleteDelegacion(t, id).then(function() {
          t.showSuccess('Delegación eliminada correctamente');
          t.loadDelegaciones();
        }).catch(function(error) {
          t.showError(error.response?.data?.message || 'Error al eliminar la delegación');
          console.error(error);
        }).finally(function() {
          t.loading = false;
        });
      },
      
      // Helpers para feedback
      showSuccess(message) {
        this.$toast.add({
          severity: 'success',
          summary: 'Éxito',
          detail: message,
          life: 3000
        });
      },
      
      showError(message) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: message,
          life: 3000
        });
      }
    }
  }
  </script>
  
  <style scoped>
  /* Estilos personalizados */
  :deep(.p-datatable) {
    font-size: 0.9rem;
  }
  
  :deep(.p-datatable .p-datatable-thead > tr > th) {
    background-color: var(--surface-50);
    font-weight: 600;
  }
  
  :deep(.p-datatable .p-datatable-tbody > tr) {
    transition: background-color 0.2s;
  }
  
  :deep(.p-datatable .p-datatable-tbody > tr:hover) {
    background-color: var(--surface-50) !important;
  }
  
  .field label {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--text-color-secondary);
  }
  
  .tag-estado {
    min-width: 80px;
    text-align: center;
  }
  </style>